import React, { useEffect, useMemo, useState } from "react";
import MultipleSelect from "./MultiSelect";
import { Select } from "antd";
import { v4 as uuidv4 } from "uuid";
import SmartIcon from "../../../../components/generic/genericIcon/SmartIcon";

const getNextMonths = (startDate = new Date(), count = 7) => {
  const monthsList = [];
  const startMonth = startDate.getMonth();
  const startYear = startDate.getFullYear();

  for (let i = 0; i < count; i++) {
    const nextMonth = new Date(startYear, startMonth + i, 1);
    monthsList.push({
      value: nextMonth.toLocaleString("default", { month: "long" }),
      checked: true,
    });
  }
  return monthsList;
};

const MonthFrequencyControl = ({
  setCarePlan,
  options,
  carePlan,
  readOnly,
  defaultMonths
}) => {
  const monthCount = Number(defaultMonths) || 7;
  const months = useMemo(
    () => getNextMonths(new Date(), monthCount),
    [monthCount],
  );
  
  const selected = months?.filter((item) => item.checked).map((item) => item.value);
  const [visitFrequency, setVisitFrequency] = useState([
    { id: 1, visitFrequency: "1-2x", months: selected },
  ]);
  const [controls, setControls] = useState([
    {
      id: 1,
      selectOptions: options,
      value: ["1-2x"],
      multiSelectOptions: months,
    },
  ]);

  const handleFrequencyChange = (id, value) => {
    const updatedControls = controls.map((control) =>
      control.id === id ? { ...control, value: value } : control
    );
    setControls(updatedControls);
    updateFrequencyData(updatedControls);
  };

  const handleMonthsChange = (id, values) => {
    const updatedControls = controls.map((control) =>
      control.id === id ? { ...control, months: values } : control
    );
    setControls(updatedControls);
    updateFrequencyData(updatedControls);
  };

  const updateFrequencyData = (controlsList) => {
    const formattedData = controlsList.map((control) => {
      return {
        visitFrequency: Array.isArray(control.value)
          ? control.value[0]
          : control.value,
        months: control.months
          ? control.months
          : control.multiSelectOptions
              ?.filter((option) => option.checked)
              .map((item) => item.value),
        id: control.id,
      };
    });
    setVisitFrequency(formattedData);
    setCarePlan((prev) => ({ ...prev, frequency: formattedData }));
  };

  const handleAddControl = () => {
    if (!readOnly) {
      if (
        visitFrequency[visitFrequency.length - 1].visitFrequency &&
        visitFrequency[visitFrequency.length - 1].months?.length
      ) {
        setControls([
          ...controls,
          {
            id: uuidv4(),
            selectOptions: options,
            multiSelectOptions: months.map((item) => ({
              ...item,
              checked: false,
            })),
          },
        ]);
      }
    }
  };

  const handleRemoveControl = (id) => {
    if (!readOnly) {
      if (controls.length > 1) {
        setControls(controls.filter((control) => control.id !== id));

        const formattedData = visitFrequency.filter((item) => item.id !== id);
        setVisitFrequency(formattedData);
        setCarePlan((prev) => ({ ...prev, frequency: formattedData }));
      }
    }
  };

  useEffect(() => {
    const carePlanFrequency =
      carePlan?.frequencyPeriod === "monthly" && carePlan?.frequency;

    if (carePlanFrequency) {
      const carePlanControl = carePlanFrequency?.map((item) => {
        const frequencyMonths = months.map((month) => ({
          ...month,
          checked: item?.months?.includes(month?.value),
        }));
        const controlObj = {
          id: item?.id,
          selectOptions: options,
          multiSelectOptions: frequencyMonths,
          value: [item?.visitFrequency],
        };
        return controlObj;
      });
      setControls(carePlanControl);
    } else {
      setControls([
        {
          id: 1,
          selectOptions: options,
          multiSelectOptions: months,
          value: ["1-2x"],
        },
      ]);
    }
  }, [carePlan?.frequency, carePlan?.frequencyPeriod, options]);

  useEffect(() => {
    setCarePlan((prev) => ({ ...prev, frequency: visitFrequency }));
  }, []);

  return (
    <>
      {controls?.map((control, index) => (
        <div key={control.id} className="mt-2 d-flex align-items-center">
          <Select
            onChange={(value) => handleFrequencyChange(control.id, value)}
            options={control.selectOptions}
            value={control.value}
            placeholder="Frequency"
            style={{
              width: 120,
            }}
            className="me-3"
            disabled={readOnly}
          />
          <MultipleSelect
            options={control.multiSelectOptions}
            placeholder="Months"
            controlId={control.id}
            handleMonthsChange={handleMonthsChange}
            readOnly={readOnly}
          />

          {!readOnly && index !== 0 && (
            <div className="add-text ms-3">
              <SmartIcon iconKey={"reject"} handleIconClick={() => handleRemoveControl(control.id)}/>
            </div>
          )}
        </div>
      ))}
      {!readOnly && (
        <div className="mt-2 add-text">
          <span onClick={handleAddControl}>Add Frequency</span>
        </div>
      )}
    </>
  );
};

export default MonthFrequencyControl;
